class SoluhogarTheme {
  constructor() {
    this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this.revealObserver = null;
    this.parallaxTargets = [];
    this.parallaxTicking = false;
    this.init(document);
    this.bindThemeEditor();
  }

  init(scope) {
    this.reveal(scope);
    this.header();
    this.mobileMenu(scope);
    this.parallax(scope);
    this.galleries(scope);
    this.quantityControls(scope);
    this.productForms(scope);
    this.quickAdd(scope);
    this.countdowns(scope);
    this.filters(scope);
    this.sliders(scope);
    this.variantSelectors(scope);
    this.mobileBuyBar(scope);
  }

  bindThemeEditor() {
    document.addEventListener('shopify:section:load', (event) => this.init(event.target));
  }

  reveal(scope) {
    const items = scope.querySelectorAll('.reveal:not([data-reveal-ready])');
    items.forEach((item) => {
      item.dataset.revealReady = 'true';
      const delay = Number(item.dataset.revealDelay || 0);
      item.style.setProperty('--reveal-delay', `${Math.min(delay, 500)}ms`);
    });

    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          this.revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -7% 0px', threshold: 0.08 });
    }
    items.forEach((item) => this.revealObserver.observe(item));
  }

  header() {
    const header = document.querySelector('[data-site-header]');
    if (!header || header.dataset.scrollReady) return;
    header.dataset.scrollReady = 'true';
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 16);
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  mobileMenu(scope) {
    const toggle = scope.querySelector('[data-menu-toggle]') || document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu || toggle.dataset.menuReady) return;
    toggle.dataset.menuReady = 'true';

    const close = () => {
      menu.classList.remove('is-open');
      document.body.classList.remove('menu-open');
      toggle.setAttribute('aria-expanded', 'false');
    };

    toggle.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      document.body.classList.toggle('menu-open', open);
      toggle.setAttribute('aria-expanded', String(open));
    });
    menu.querySelectorAll('a').forEach((link) => link.addEventListener('click', close));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
  }

  parallax(scope) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    scope.querySelectorAll('[data-parallax]:not([data-parallax-ready])').forEach((target) => {
      target.dataset.parallaxReady = 'true';
      this.parallaxTargets.push(target);
    });
    if (!this.parallaxTargets.length || this.parallaxBound) return;
    this.parallaxBound = true;

    const update = () => {
      this.parallaxTargets = this.parallaxTargets.filter((target) => document.contains(target));
      this.parallaxTargets.forEach((target) => {
        const rect = target.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;
        const centerDistance = window.innerHeight / 2 - (rect.top + rect.height / 2);
        const offset = Math.max(-18, Math.min(18, centerDistance * 0.035));
        target.style.transform = `translate3d(0, ${offset}px, 0)`;
      });
      this.parallaxTicking = false;
    };

    window.addEventListener('scroll', () => {
      if (this.parallaxTicking) return;
      requestAnimationFrame(update);
      this.parallaxTicking = true;
    }, { passive: true });
    update();
  }

  galleries(scope) {
    scope.querySelectorAll('[data-gallery]:not([data-gallery-ready])').forEach((gallery) => {
      gallery.dataset.galleryReady = 'true';
      const main = gallery.querySelector('[data-gallery-main]');
      gallery.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const template = thumb.querySelector('template');
          if (!template || !main) return;
          main.innerHTML = template.innerHTML;
          gallery.querySelectorAll('[data-gallery-thumb]').forEach((item) => {
            const active = item === thumb;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
        });
      });
    });
  }

  quantityControls(scope) {
    scope.querySelectorAll('[data-product-section]:not([data-quantity-ready])').forEach((section) => {
      section.dataset.quantityReady = 'true';
      const input = section.querySelector('[data-quantity-input]');
      if (!input) return;

      section.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          input.value = Math.max(1, Number(input.value || 1) + Number(button.dataset.quantityChange));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      section.querySelectorAll('[data-quantity-offer]').forEach((offer) => {
        offer.addEventListener('change', () => {
          input.value = Math.max(1, Number(offer.dataset.quantity || 1));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      const selected = section.querySelector('[data-quantity-offer]:checked');
      if (selected) input.value = Math.max(1, Number(selected.dataset.quantity || 1));
    });
  }

  productForms(scope) {
    scope.querySelectorAll('.product-form:not([data-product-form-ready])').forEach((form) => {
      form.dataset.productFormReady = 'true';
      form.addEventListener('submit', async (event) => {
        if (!window.fetch) return;
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        const message = form.querySelector('[data-form-message]');
        const original = button.textContent;
        button.disabled = true;
        button.textContent = 'PROCESANDO…';
        if (message) message.textContent = '';

        try {
          const response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'No pudimos agregar este producto. Revisa la variante o el stock.');
          window.location.assign(`${this.root}checkout`);
        } catch (error) {
          if (message) message.textContent = error.message;
          button.disabled = false;
          button.textContent = original;
        }
      });
    });
  }

  quickAdd(scope) {
    scope.querySelectorAll('[data-quick-add-form]:not([data-quick-add-ready])').forEach((form) => {
      form.dataset.quickAddReady = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        const original = button.textContent;
        button.disabled = true;
        button.textContent = 'Agregando…';

        try {
          const response = await fetch(`${this.root}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'No se pudo agregar');
          button.textContent = 'Agregado ✓';
          this.refreshCartCount();
          this.showToast(`${result.product_title || 'Producto'} agregado al carro`);
          setTimeout(() => { button.disabled = false; button.textContent = original; }, 1800);
        } catch (error) {
          button.disabled = false;
          button.textContent = 'Reintentar';
          this.showToast(error.message);
          setTimeout(() => { button.textContent = original; }, 1800);
        }
      });
    });
  }

  async refreshCartCount() {
    try {
      const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((counter) => {
        counter.textContent = cart.item_count;
        counter.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (_) {}
  }

  showToast(text) {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    const output = toast.querySelector('[data-cart-toast-text]');
    if (output) output.textContent = text;
    toast.classList.add('is-visible');
    clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  }

  countdowns(scope) {
    scope.querySelectorAll('[data-countdown]:not([data-countdown-ready])').forEach((timer) => {
      timer.dataset.countdownReady = 'true';
      const output = timer.querySelector('[data-countdown-time]');
      const configured = Date.parse(timer.dataset.deadline || '');
      const duration = Math.max(30, Number(timer.dataset.countdownMinutes || 360)) * 60000;
      const key = `soluhogar-countdown-${timer.dataset.countdownId || 'offer'}`;
      let deadline = configured;

      if (!deadline) {
        try {
          deadline = Number(sessionStorage.getItem(key));
          if (!deadline || deadline <= Date.now()) {
            deadline = Date.now() + duration;
            sessionStorage.setItem(key, String(deadline));
          }
        } catch (_) {
          deadline = Date.now() + duration;
        }
      }
      if (!deadline || !output) return;
      timer.hidden = false;

      let interval = null;
      const tick = () => {
        const distance = Math.max(0, deadline - Date.now());
        const days = Math.floor(distance / 86400000);
        const hours = Math.floor((distance % 86400000) / 3600000);
        const minutes = Math.floor((distance % 3600000) / 60000);
        const seconds = Math.floor((distance % 60000) / 1000);
        output.textContent = `${days ? `${days}d ` : ''}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        if (distance === 0) {
          timer.classList.add('is-ended');
          output.textContent = 'FINALIZADA';
          if (interval) clearInterval(interval);
        }
      };
      tick();
      if (deadline > Date.now()) interval = setInterval(tick, 1000);
    });
  }

  filters(scope) {
    scope.querySelectorAll('[data-category-filter]:not([data-filter-ready])').forEach((container) => {
      container.dataset.filterReady = 'true';
      const section = container.closest('section') || document;
      const buttons = container.querySelectorAll('[data-filter]');
      const items = section.querySelectorAll('[data-filter-item]');

      buttons.forEach((button) => button.addEventListener('click', () => {
        const filter = button.dataset.filter;
        buttons.forEach((item) => item.classList.toggle('is-active', item === button));
        items.forEach((item) => {
          const category = item.dataset.category || '';
          const visible = filter === 'todos' || category.includes(filter);
          item.hidden = !visible;
        });
      }));
    });
  }

  sliders(scope) {
    scope.querySelectorAll('[data-slider]:not([data-slider-ready])').forEach((slider) => {
      slider.dataset.sliderReady = 'true';
      const section = slider.closest('section');
      const prev = section && section.querySelector('[data-slider-prev]');
      const next = section && section.querySelector('[data-slider-next]');
      const move = (direction) => slider.scrollBy({ left: slider.clientWidth * .82 * direction, behavior: 'smooth' });
      if (prev) prev.addEventListener('click', () => move(-1));
      if (next) next.addEventListener('click', () => move(1));
      slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') move(-1);
        if (event.key === 'ArrowRight') move(1);
      });
    });
  }

  variantSelectors(scope) {
    scope.querySelectorAll('[data-variant-select]:not([data-variant-ready])').forEach((select) => {
      select.dataset.variantReady = 'true';
      const section = select.closest('[data-product-section]');
      const button = section && section.querySelector('[data-product-submit]');
      const currentPrice = section && section.querySelector('[data-product-price] .price__current');
      const stickyPrice = section && section.querySelector('[data-mobile-buy-bar] strong');

      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const available = option.dataset.available === 'true';
        if (currentPrice && option.dataset.price) currentPrice.textContent = option.dataset.price;
        if (stickyPrice && option.dataset.price) stickyPrice.textContent = option.dataset.price;
        if (button) {
          button.disabled = !available;
          button.textContent = available ? 'PAGA AL RECIBIR' : 'AGOTADO';
        }
      });
    });
  }

  mobileBuyBar(scope) {
    scope.querySelectorAll('[data-mobile-buy-bar]:not([data-mobile-bar-ready])').forEach((bar) => {
      bar.dataset.mobileBarReady = 'true';
      const section = bar.closest('[data-product-section]');
      const form = section && section.querySelector('.product-form');
      const buyButton = form && form.querySelector('[data-product-submit]');
      const scrollButton = bar.querySelector('[data-scroll-to-form]');
      if (!form || !buyButton) return;

      if (scrollButton) scrollButton.addEventListener('click', () => {
        if (!buyButton.disabled) buyButton.click();
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          bar.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 320);
        }, { threshold: .15 });
        observer.observe(buyButton);
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new SoluhogarTheme());
